import type { MetaFunction } from "react-router";
import { Link } from "react-router";
import { useEffect, useMemo, useState } from "react";
import {
  PHONETIC_LANGUAGE_PAIRS,
  PHONETIC_PAIR_STORAGE_KEY,
  detectPairFromBrowserLanguage,
  isSupportedPair,
  suggestResolverTask,
  type LanguagePair,
  type ResolverTask,
} from "~/lib/phonetic-resolver";

type PhoneticCandidate = {
  text: string;
  confidence: number;
};

type PhoneticResult = {
  engine: "PhoneticResolver";
  reading: string;
  candidates: PhoneticCandidate[];
};

type TranslateResult = {
  casual?: string;
  formal?: string;
  conversational?: string;
  confidence?: number;
  explanation?: string;
};

type ResolverResponsePayload = {
  success: boolean;
  error?: { code: string; message: string };
  result?: PhoneticResult | TranslateResult;
};

function isPhoneticResultPayload(
  value: PhoneticResult | TranslateResult | null,
): value is PhoneticResult {
  return Boolean(value && "engine" in value && value.engine === "PhoneticResolver");
}

export const meta: MetaFunction = () => {
  return [
    { title: "Phonetic Resolver - Fluentry" },
    {
      name: "description",
      content:
        "Resolve ambiguous heard sounds into canonical words with context-aware candidate ranking.",
    },
  ];
};

function pairToValue(pair: LanguagePair): string {
  return `${pair.sourceLanguage}->${pair.targetLanguage}`;
}

function valueToPair(value: string): LanguagePair | null {
  const [sourceLanguage, targetLanguage] = value.split("->");
  if (!sourceLanguage || !targetLanguage) return null;
  const pair = { sourceLanguage, targetLanguage };
  return isSupportedPair(pair) ? pair : null;
}

export default function PhoneticResolverPage() {
  const [task, setTask] = useState<ResolverTask>("phonetic_resolve");
  const [input, setInput] = useState("");
  const [context, setContext] = useState("");
  const [uiLanguage, setUiLanguage] = useState("ja");
  const [priority, setPriority] = useState<"quality" | "latency" | "cost">("quality");
  const [pair, setPair] = useState<LanguagePair>({ sourceLanguage: "en", targetLanguage: "zh" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PhoneticResult | TranslateResult | null>(null);
  const [rawJson, setRawJson] = useState<string>("");

  useEffect(() => {
    try {
      const saved = localStorage.getItem(PHONETIC_PAIR_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as LanguagePair;
        if (isSupportedPair(parsed)) {
          setPair(parsed);
          return;
        }
      }
    } catch {
      // no-op
    }

    const detected = detectPairFromBrowserLanguage(navigator.language);
    setPair(detected);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(PHONETIC_PAIR_STORAGE_KEY, JSON.stringify(pair));
    } catch {
      // no-op
    }
  }, [pair]);

  const suggestion = useMemo(() => suggestResolverTask(input), [input]);

  const canSubmit = input.trim().length > 0 && !loading;

  async function submit() {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const endpoint = task === "phonetic_resolve" ? "/api/phonetic-resolve" : "/api/convert";
      const requestBody =
        task === "phonetic_resolve"
          ? {
              reading: input,
              contextText: context || undefined,
              maxCandidates: 3,
            }
          : {
              input,
              targetLanguage: pair.targetLanguage,
              includeExplanation: false,
              sourceLanguage: pair.sourceLanguage,
              uiLanguage,
              priority,
            };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const payload = (await response.json()) as ResolverResponsePayload;
      setRawJson(JSON.stringify(payload, null, 2));

      if (!response.ok || !payload?.success) {
        const message = payload?.error?.message || "Request failed";
        setError(String(message));
        return;
      }

      setResult(payload.result ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network request failed");
    } finally {
      setLoading(false);
    }
  }

  const phoneticResult = isPhoneticResultPayload(result) ? result : null;
  const translateResult = result && !isPhoneticResultPayload(result) ? result : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-cyan-50">
      <div className="mx-auto max-w-5xl px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Phonetic Resolver</h1>
            <p className="mt-2 text-gray-600">
              聞き取った音を文脈付きで推定し、正規語形候補を返します。
            </p>
          </div>
          <Link
            to="/"
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Back to Home
          </Link>
        </div>

        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex gap-2">
            <button
              type="button"
              onClick={() => setTask("phonetic_resolve")}
              className={`rounded-md px-4 py-2 text-sm font-medium ${
                task === "phonetic_resolve"
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              聞き取り語推定
            </button>
            <button
              type="button"
              onClick={() => setTask("translate")}
              className={`rounded-md px-4 py-2 text-sm font-medium ${
                task === "translate"
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              文章変換
            </button>
          </div>

          <div className="mb-4 rounded-md border border-indigo-100 bg-indigo-50 p-3 text-sm text-indigo-900">
            おすすめモード:{" "}
            <span className="font-semibold">
              {suggestion.task === "phonetic_resolve" ? "聞き取り語推定" : "文章変換"}
            </span>
            <div className="mt-1">{suggestion.reason}</div>
            {suggestion.task !== task && (
              <button
                type="button"
                onClick={() => setTask(suggestion.task)}
                className="mt-2 rounded bg-white px-3 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-100"
              >
                おすすめモードに切り替える
              </button>
            )}
          </div>

          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">言語ペア</label>
              <select
                value={pairToValue(pair)}
                onChange={(e) => {
                  const next = valueToPair(e.target.value);
                  if (next) setPair(next);
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {PHONETIC_LANGUAGE_PAIRS.map((item) => (
                  <option key={pairToValue(item)} value={pairToValue(item)}>
                    {item.sourceLanguage} → {item.targetLanguage}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">UI language</label>
              <select
                value={uiLanguage}
                onChange={(e) => setUiLanguage(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="ja">ja</option>
                <option value="en">en</option>
                <option value="zh">zh</option>
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as "quality" | "latency" | "cost")}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="quality">quality</option>
                <option value="latency">latency</option>
                <option value="cost">cost</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                className="w-full rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {loading ? "Resolving..." : "実行"}
              </button>
            </div>
          </div>

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-gray-700">入力</label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="例: すいじゃお / suizyao"
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              文脈（任意: phonetic_resolve時に有効）
            </label>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="例: 昨天太累了，我想早点___"
              rows={2}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {result && (
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-xl font-semibold text-gray-900">Result</h2>

            {phoneticResult ? (
              <div className="space-y-3">
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  engine: <span className="font-semibold">{phoneticResult.engine}</span>
                </div>
                <div className="rounded-md border border-gray-200 p-3 text-sm text-gray-700">
                  reading: <span className="font-mono">{phoneticResult.reading}</span>
                </div>
                {phoneticResult.candidates.map((candidate, index) => (
                  <div key={`${candidate.text}-${index}`} className="rounded-md border border-gray-200 p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-lg font-semibold text-gray-900">{candidate.text}</p>
                      <p className="text-sm text-gray-500">
                        {Math.round(candidate.confidence * 100)}%
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-md border border-gray-200 p-3">
                  <p className="text-sm font-medium text-gray-500">Casual</p>
                  <p className="text-gray-900">{translateResult?.casual}</p>
                </div>
                <div className="rounded-md border border-gray-200 p-3">
                  <p className="text-sm font-medium text-gray-500">Formal</p>
                  <p className="text-gray-900">{translateResult?.formal}</p>
                </div>
                <div className="rounded-md border border-gray-200 p-3">
                  <p className="text-sm font-medium text-gray-500">Conversational</p>
                  <p className="text-gray-900">{translateResult?.conversational}</p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="rounded-xl border border-gray-200 bg-gray-900 p-5 shadow-sm">
          <h2 className="mb-3 text-lg font-semibold text-white">Debug JSON</h2>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap text-xs text-gray-200">
            {rawJson || "{\n  \"message\": \"No response yet\"\n}"}
          </pre>
        </div>
      </div>
    </div>
  );
}
